import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight, MapPin, Phone, Mail, X, Globe, LayoutGrid, Code, TrendingUp, Zap, MessageCircle } from 'lucide-react';
import logo2 from '../assets/3D Logo (1).png';

const Footer = () => {
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [activeService, setActiveService] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setActiveService(null);
  }, [location.pathname]);

  return (
    <footer className="relative bg-[#020617] text-gray-300 pt-10 mt-auto overflow-hidden" style={{ fontFamily: 'emoji' }}>

      {/* Decorative Top Line */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#0EA5E9] via-emerald-400 to-violet-500" />

      {/* Subtle Background Glows */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#0EA5E9]/5 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-violet-500/5 rounded-full blur-[100px] pointer-events-none" />

      <div className="max-w-[90rem] mx-auto px-6 sm:px-8 lg:px-12 relative z-10">

        {/* Main Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-x-8 gap-y-10 pb-8">

          {/* Column 1: Brand details */}
          <div className="lg:col-span-3 flex flex-col items-start">
            <Link to="/" className="inline-block mb-4">
              <img src={logo2} alt="VishwaDigitech Logo" className="h-16 w-auto object-contain drop-shadow-[0_0_15px_rgba(14,165,233,0.15)]" />
            </Link>
            <p className="text-sm text-gray-400 leading-relaxed mb-6 max-w-sm">
              Empowering businesses with innovative Digital Marketing and IT Solutions that drive growth, innovation, and success.
            </p>
            <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-4 w-full max-w-sm relative group overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-[#0EA5E9]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <h5 className="text-white font-semibold mb-2 relative z-10">Let's talk business</h5>
              <a href="mailto:info@vishwadigitech.com" className="text-[#0EA5E9] hover:text-white transition-colors duration-300 relative z-10 font-medium">info@vishwadigitech.com</a>
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div className="lg:col-span-2">
            <h4 className="text-white font-bold text-lg mb-5 relative inline-block">
              Company
              <span className="absolute -bottom-2 left-0 w-1/2 h-0.5 bg-[#0EA5E9] rounded-full" />
            </h4>
            <ul className="space-y-3">
              {[
                { name: 'About Us', path: '/about' },
                { name: 'Services', path: '/services/websites' },
                { name: 'Blogs', path: '/blogs' },
                { name: 'Contact Us', path: '/contact' }
              ].map(link => (
                <li key={link.name}>
                  <Link to={link.path} className="group flex items-center text-gray-400 hover:text-white transition-all duration-300 w-fit">
                    <span className="w-0 h-0.5 bg-[#0EA5E9] mr-0 group-hover:w-3 group-hover:mr-3 transition-all duration-300 rounded-full" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Services */}
          <div className="lg:col-span-4">
            <div className="w-full text-left sm:text-center mb-5 sm:mb-6">
              <h4 className="text-white font-bold text-lg relative inline-block">
                Services
                {/* Mobile underline */}
                <span className="absolute -bottom-2 left-0 w-1/2 h-0.5 bg-violet-500 rounded-full sm:hidden" />
                {/* Laptop/Tablet underline */}
                <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-[200%] h-0.5 bg-gradient-to-r from-transparent via-violet-500 to-transparent rounded-full hidden sm:block" />
              </h4>
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-6 text-sm sm:px-4">
              {[
                { 
                  name: 'Website Development', 
                  path: '/services/websites', 
                  icon: Globe,
                  items: [
                    { name: 'Static Website Development', href: '/services/websites' },
                    { name: 'Dynamic Website Development', href: '/services/websites' },
                    { name: 'Ecommerce Website', href: '/services/ecommerce' },
                  ]
                },
                { name: 'Application Development', path: '/services/apps', icon: LayoutGrid },
                { name: 'Custom Software', path: '/services/software', icon: Code },
                { 
                  name: 'Digital Marketing', 
                  path: '/services/marketing', 
                  icon: TrendingUp,
                  items: [
                    { name: 'SEO SEM', href: '/services/marketing' },
                    { name: 'Social Media Marketing', href: '/services/social-media-marketing' },
                    { name: 'Graphics & Video Production', href: '/services/graphics-designing' },
                    { name: 'GMB Optimization', href: '/services/marketing' },
                  ]
                },
                { 
                  name: 'Performance Marketing', 
                  path: '/services/marketing', 
                  icon: Zap,
                  items: [
                    { name: 'Meta Ads', href: '/services/marketing' },
                    { name: 'Google Ads', href: '/services/marketing' },
                    { name: 'Hostar Ads', href: '/services/marketing' },
                  ]
                },
                { name: 'Whatsapp API', path: '/services/whatsapp-api', icon: MessageCircle },
              ].map(link => (
                <li key={link.name} className="flex flex-col gap-1">
                  <div 
                    onClick={() => {
                      if (link.items) {
                        setActiveService(activeService === link.name ? null : link.name);
                      } else {
                        setActiveService(null);
                        navigate(link.path);
                        window.scrollTo(0, 0);
                      }
                    }}
                    className="group flex items-center justify-between w-full sm:w-[90%] cursor-pointer py-1"
                  >
                    <div className="flex items-center gap-3 text-gray-200 group-hover:text-white transition-all duration-300 font-medium">
                      <div className="p-1.5 rounded-lg bg-white/5 group-hover:bg-[#0EA5E9]/20 group-hover:shadow-[0_0_10px_rgba(14,165,233,0.3)] transition-all duration-300">
                        <link.icon className="w-4 h-4 text-[#0EA5E9] group-hover:scale-110 transition-transform" />
                      </div>
                      <span className="group-hover:translate-x-1 transition-transform">{link.name}</span>
                    </div>
                    {link.items && (
                      <ChevronDown className={`w-4 h-4 transition-transform duration-300 text-gray-500 group-hover:text-white ${activeService === link.name ? 'rotate-180 text-[#0EA5E9]' : ''}`} />
                    )}
                  </div>
                  
                  {link.items && link.items.length > 0 && (
                    <div className={`overflow-hidden transition-all duration-300 ${activeService === link.name ? 'max-h-[500px] opacity-100 mb-2' : 'max-h-0 opacity-0'}`}>
                      <div className="flex flex-col gap-2.5 pl-10 border-l border-white/5 ml-3 pt-1 pb-1">
                        {link.items.map((item, i) => (
                          <Link 
                            key={i} 
                            to={item.href} 
                            onClick={() => {
                              setActiveService(null);
                              window.scrollTo(0, 0);
                            }}
                            className="text-xs text-gray-400 hover:text-[#0EA5E9] transition-colors flex items-center gap-2 group/sub"
                          >
                            <div className="w-1 h-1 rounded-full bg-[#0EA5E9]/30 group-hover/sub:bg-[#0EA5E9] group-hover/sub:scale-150 transition-all duration-300" />
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Contact Info & Socials */}
          <div className="lg:col-span-3">
            <h4 className="text-white font-bold text-lg mb-5 relative inline-block">
              Reach Out
              <span className="absolute -bottom-2 left-0 w-1/2 h-0.5 bg-emerald-400 rounded-full" />
            </h4>

            <div className="space-y-4 mb-6 text-sm">
              <div onClick={() => setIsMapOpen(true)} className="flex items-start gap-4 group cursor-pointer">
                <div className="mt-1 text-emerald-400 group-hover:scale-110 transition-transform"><MapPin className="w-5 h-5" /></div>
                <p className="text-gray-400 leading-relaxed group-hover:text-white transition-colors">2nd floor, V S Khandekar Vachanalay, Sangli, Khanbhag, Sangli Miraj Kupwad, Maharashtra 416416</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-[#0EA5E9]"><Phone className="w-5 h-5" /></div>
                <a href="tel:+919699938509" className="text-gray-400 hover:text-white transition-colors">+91 9699938509</a>
              </div>
            </div>

            {/* Glassmorphic Social Icons */}
            <div className="flex items-center gap-3">
              <a
                href="https://www.facebook.com/vishwadigitech"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-xl bg-white/[0.03] border border-white/10 flex items-center justify-center text-gray-400 hover:bg-[#1877F2] hover:text-white hover:border-[#1877F2] transition-all duration-300 hover:-translate-y-1 shadow-lg"
              >
                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M22.675 0h-21.35c-.732 0-1.325.593-1.325 1.325v21.351c0 .731.593 1.324 1.325 1.324h11.495v-9.294h-3.128v-3.622h3.128v-2.671c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12v9.293h6.116c.73 0 1.323-.593 1.323-1.325v-21.35c0-.732-.593-1.325-1.325-1.325z" /></svg>
              </a>
              <a
                href="https://www.youtube.com/@VishwaDigiTech7"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-xl bg-white/[0.03] border border-white/10 flex items-center justify-center text-gray-400 hover:bg-[#FF0000] hover:text-white hover:border-[#FF0000] transition-all duration-300 hover:-translate-y-1 shadow-lg"
              >
                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" /></svg>
              </a>
              <a
                href="https://www.instagram.com/vishwadigitech?igsh=c3M2cWs0cXV2bmJz"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-xl bg-white/[0.03] border border-white/10 flex items-center justify-center text-gray-400 hover:bg-[#E4405F] hover:text-white hover:border-[#E4405F] transition-all duration-300 hover:-translate-y-1 shadow-lg"
              >
                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" /></svg>
              </a>
              <a
                href="https://www.linkedin.com/company/vishwa-digitech"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-xl bg-white/[0.03] border border-white/10 flex items-center justify-center text-gray-400 hover:bg-[#0A66C2] hover:text-white hover:border-[#0A66C2] transition-all duration-300 hover:-translate-y-1 shadow-lg"
              >
                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" /></svg>
              </a>
            </div>
          </div>

        </div>

        {/* Footer Bottom */}
        <div className="border-t border-white/5 py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-gray-500">
            © {new Date().getFullYear()} <span className="text-gray-300 font-medium">VishwaDigitech</span>. All rights reserved.
          </p>
          <div className="flex gap-6 text-sm">
            <Link to="/privacy-policy" className="text-gray-500 hover:text-[#0EA5E9] transition-colors">Privacy Policy</Link>
            <Link to="/terms-of-service" className="text-gray-500 hover:text-[#0EA5E9] transition-colors">Terms of Service</Link>
          </div>
        </div>
      </div>

      {/* Map Modal */}
      {isMapOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-black/80 backdrop-blur-sm">
          {/* Overlay Click to Close */}
          <div className="absolute inset-0" onClick={() => setIsMapOpen(false)}></div>
          
          {/* Modal Content */}
          <div className="relative w-full max-w-4xl bg-[#0F1629] border border-white/10 rounded-2xl overflow-hidden shadow-2xl flex flex-col">
            
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10 bg-[#0A0F1F]">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#0EA5E9]" />
                Our Location
              </h3>
              <button 
                onClick={() => setIsMapOpen(false)}
                className="p-1 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {/* Map Container */}
            <div className="relative w-full h-[60vh] sm:h-[70vh]">
              <iframe
                src="https://maps.google.com/maps?q=Ram%20Mandir,%20Sangli,%20Maharashtra&t=&z=15&ie=UTF8&iwloc=&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0, filter: 'invert(100%) hue-rotate(180deg) brightness(85%) contrast(85%) grayscale(20%)' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="VishwaDigitech Location"
                className="absolute inset-0"
              ></iframe>
            </div>
          </div>
        </div>
      )}
    </footer>
  );
};

export default Footer;
